/**
 * Whether a map can actually be raced. Every rule here mirrors a real behaviour in `race.ts` or in
 * GameScene's placement, rather than an opinion about map design: an author should be able to
 * ignore every warning and still finish a lap, and should not be able to ignore an error and do so.
 *
 * Nothing calls this on load. A half-built map is the normal state of a map being built, so the
 * editor reports and the player screen never refuses.
 */

import { gateWidth } from './checkpoints.ts';
import { pointInPolygon } from './islands.ts';
import { tessellateRing } from './ring.ts';
import { startAreaCentroid } from './startArea.ts';
import type { ObjectRef } from './selection.ts';
import type { MapDocument } from './types.ts';

export type IssueSeverity = 'error' | 'warning';

export type MapIssue = {
  /** Stable across re-validations of the same fault, so the panel can key on it. */
  id: string;
  severity: IssueSeverity;
  message: string;
  /** The object to select when the issue is clicked, when one object is to blame. */
  ref: ObjectRef | null;
};

export type MapValidation = {
  issues: MapIssue[];
  errorCount: number;
  warningCount: number;
  /** No errors. Warnings do not block a race. */
  playable: boolean;
};

/** Matches `race.ts`: below this the two flags are one point and the gate is crossed by proximity. */
const DEGENERATE_GATE_WIDTH = 1e-6;

export function validateMap(map: MapDocument): MapValidation {
  const issues = [
    ...checkpointIssues(map),
    ...startAreaIssues(map),
    ...boundsIssues(map),
  ];

  const errorCount = issues.filter((issue) => issue.severity === 'error').length;

  return {
    issues,
    errorCount,
    warningCount: issues.length - errorCount,
    playable: errorCount === 0,
  };
}

function checkpointIssues(map: MapDocument): MapIssue[] {
  const issues: MapIssue[] = [];

  if (map.checkpoints.length === 0) {
    issues.push({
      id: 'no-checkpoints',
      severity: 'error',
      message: 'No gates. Add at least one and mark it the goal.',
      ref: null,
    });

    return issues;
  }

  const goals = map.checkpoints.filter((checkpoint) => checkpoint.isGoal);

  if (goals.length === 0) {
    // `isRaceComplete` starts by looking for the goal, so without one the race never ends.
    issues.push({
      id: 'no-goal',
      severity: 'error',
      message: 'No goal gate. The race can never be completed.',
      ref: null,
    });
  }

  for (const checkpoint of map.checkpoints) {
    if (gateWidth(checkpoint) <= DEGENERATE_GATE_WIDTH) {
      issues.push({
        id: `point-gate:${checkpoint.id}`,
        severity: 'warning',
        message: 'Gate is a single point, so it is passed by sailing within 16 units of it.',
        ref: { kind: 'checkpoint', id: checkpoint.id },
      });
    }
  }

  return issues;
}

function startAreaIssues(map: MapDocument): MapIssue[] {
  if (!map.startArea) {
    // GameScene drops the boat on the map centroid instead, which is playable but arbitrary.
    return [
      {
        id: 'no-start-area',
        severity: 'warning',
        message: 'No start area. The boat will be dropped at the centre of the map.',
        ref: null,
      },
    ];
  }

  const centroid = startAreaCentroid(map);
  const onLand = map.islands.some((island) => pointInPolygon(centroid, tessellateRing(island)));
  const onRock = map.rocks.some((rock) => pointInPolygon(centroid, tessellateRing(rock)));

  if (onLand || onRock) {
    return [
      {
        id: 'start-area-on-land',
        severity: 'warning',
        message: 'The start area overlaps land. The boat can be placed on an island or rock.',
        ref: { kind: 'startArea' },
      },
    ];
  }

  return [];
}

/** Objects outside the bounds still render, but the camera will not scroll far enough to reach them. */
function boundsIssues(map: MapDocument): MapIssue[] {
  const { width, height } = map.bounds;
  const outside = (x: number, y: number): boolean => x < 0 || y < 0 || x > width || y > height;
  const issues: MapIssue[] = [];

  for (const checkpoint of map.checkpoints) {
    if (outside(checkpoint.a.x, checkpoint.a.y) || outside(checkpoint.b.x, checkpoint.b.y)) {
      issues.push({
        id: `gate-out-of-bounds:${checkpoint.id}`,
        severity: 'error',
        message: 'Gate lies outside the map bounds and cannot be reached.',
        ref: { kind: 'checkpoint', id: checkpoint.id },
      });
    }
  }

  return issues;
}
